import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  modalityTerms,
  pipes,
  type PipeSummary,
  type RequiredDocument,
  type RequiredDocumentInput,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDate } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { campoRecusado } from './textos';
import { Marcacao } from './CondicoesParticulares';

type Escopo = 'modalidade' | 'solicitacao_limite';

/**
 * As esteiras da empresa, para escolher em quais o documento da modalidade vale
 * (PAR-091, PipeIdList do OnPoint). A lista exige `esteiras.esteira.view`: sem ela, a
 * consulta falha, e as esteiras gravadas ficam como estao.
 */
function useEsteiras(ligada: boolean) {
  return useQuery({
    queryKey: ['pipes'],
    queryFn: ({ signal }) => pipes.list(signal),
    enabled: ligada,
    retry: false,
  });
}

function nomesDasEsteiras(ids: string[], lista: PipeSummary[] | undefined): string {
  if (ids.length === 0) return 'Todas';
  if (!lista) return `${ids.length} ${ids.length === 1 ? 'esteira' : 'esteiras'}`;
  const porId = new Map(lista.map((esteira) => [esteira.id, esteira.name]));
  return ids.map((id) => porId.get(id) ?? 'Esteira fora da lista').join(', ');
}

/**
 * Caixas das esteiras do documento. Nenhuma marcada, o documento vale para todas, como
 * no OnPoint. A esteira gravada que nao veio na lista continua marcada e e enviada de
 * volta, para nao sumir sem ninguem pedir.
 */
function EscolhaDasEsteiras({
  escolhidas,
  onChange,
  lista,
  carregando,
  falhou,
}: {
  escolhidas: string[];
  onChange: (ids: string[]) => void;
  lista: PipeSummary[] | undefined;
  carregando: boolean;
  falhou: boolean;
}) {
  const conhecidas = new Set((lista ?? []).map((esteira) => esteira.id));
  const foraDaLista = escolhidas.filter((id) => !conhecidas.has(id)).length;
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-xs font-medium text-ink-700">Esteiras</legend>
      {carregando ? (
        <p className="text-2xs text-ink-500">Carregando as esteiras...</p>
      ) : falhou ? (
        <p className="text-2xs text-ink-500">
          A lista de esteiras nao abriu (ela exige a leitura das esteiras). As esteiras
          do documento ficam como estao: {nomesDasEsteiras(escolhidas, undefined)}.
        </p>
      ) : (
        <>
          <p className="text-2xs text-ink-500">
            Sem nenhuma marcada, o documento vale para todas as esteiras.
          </p>
          <ul className="flex flex-col gap-1" aria-label="Esteiras do documento">
            {(lista ?? []).map((esteira) => (
              <li key={esteira.id}>
                <label className="flex items-center gap-2 text-sm text-ink-800">
                  <input
                    type="checkbox"
                    className="size-4 accent-[var(--color-brand-500)]"
                    checked={escolhidas.includes(esteira.id)}
                    onChange={(evento) =>
                      onChange(
                        evento.target.checked
                          ? [...escolhidas, esteira.id]
                          : escolhidas.filter((id) => id !== esteira.id),
                      )
                    }
                  />
                  {esteira.name}
                </label>
              </li>
            ))}
          </ul>
          {foraDaLista > 0 ? (
            <p className="text-2xs text-ink-500">
              Esteiras gravadas fora da lista, que continuam no documento: {foraDaLista}
              .
            </p>
          ) : null}
        </>
      )}
    </fieldset>
  );
}

function FormularioDoDocumento({
  escopo,
  modalityId,
  documento,
  onFechar,
  onSalvo,
}: {
  escopo: Escopo;
  modalityId: string;
  documento: RequiredDocument | null;
  onFechar: () => void;
  onSalvo: () => void;
}) {
  const [nome, setNome] = useState(documento?.name ?? '');
  const [orientacao, setOrientacao] = useState(documento?.description ?? '');
  const [obrigatorio, setObrigatorio] = useState(documento?.is_required ?? false);
  const [escolhidas, setEscolhidas] = useState<string[]>(documento?.pipe_ids ?? []);
  const esteiras = useEsteiras(escopo === 'modalidade');

  const salvar = useMutation({
    mutationFn: () => {
      const corpo: RequiredDocumentInput = {
        name: nome.trim(),
        description: orientacao.trim(),
        is_required: obrigatorio,
      };
      // As esteiras so vao quando a lista abriu: sem ela, quem edita nao ve o que esta
      // marcado, e as esteiras gravadas ficam como estao (PAR-091).
      if (escopo === 'modalidade' && esteiras.data) {
        corpo.pipe_ids = escolhidas;
      }
      if (escopo === 'solicitacao_limite') {
        return documento
          ? modalityTerms.updateLimitDocument(documento.id, corpo)
          : modalityTerms.createLimitDocument(corpo);
      }
      return documento
        ? modalityTerms.updateRequiredDocument(modalityId, documento.id, corpo)
        : modalityTerms.createRequiredDocument(modalityId, corpo);
    },
    onSuccess: onSalvo,
  });
  const apiError = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <Modal
      open
      onClose={onFechar}
      title={documento ? `Editar documento: ${documento.name}` : 'Incluir documento'}
      description={
        escopo === 'solicitacao_limite'
          ? 'Esta lista vale para a seguradora inteira, nao apenas para esta modalidade.'
          : 'Documento que a modalidade exige, nas esteiras escolhidas. Nenhuma esteira cobra o documento ainda.'
      }
    >
      <form className="flex flex-col gap-4" onSubmit={enviar} noValidate>
        {salvar.error && !campoRecusado(apiError) ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {salvar.error.message}
          </Alert>
        ) : null}
        <Field
          label="Nome do documento"
          required
          value={nome}
          maxLength={200}
          error={fieldErrorOf(apiError, 'name')}
          onChange={(e) => setNome(e.target.value)}
        />
        <Textarea
          label="Orientacao"
          value={orientacao}
          maxLength={2000}
          error={fieldErrorOf(apiError, 'description')}
          onChange={(e) => setOrientacao(e.target.value)}
        />
        <Marcacao
          label="Obrigatorio"
          checked={obrigatorio}
          onChange={setObrigatorio}
          disabled={false}
        />
        {escopo === 'modalidade' ? (
          <EscolhaDasEsteiras
            escolhidas={escolhidas}
            onChange={setEscolhidas}
            lista={esteiras.data}
            carregando={esteiras.isPending}
            falhou={esteiras.isError}
          />
        ) : null}
        {fieldErrorOf(apiError, 'pipe_ids') ? (
          <Alert tone="danger" title="Esteiras recusadas">
            {fieldErrorOf(apiError, 'pipe_ids')}
          </Alert>
        ) : null}
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Fechar
          </Button>
          <Button type="submit" loading={salvar.isPending}>
            Salvar documento
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function TabelaDeDocumentos({
  escopo,
  modalityId,
}: {
  escopo: Escopo;
  modalityId: string;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeCriar = can('parametrizacao.modalidade.create');
  const podeEditar = can('parametrizacao.modalidade.update');
  const podeExcluir =
    can('parametrizacao.modalidade.delete') && escopo === 'modalidade';
  const [editando, setEditando] = useState<RequiredDocument | 'novo' | null>(null);
  const [excluindo, setExcluindo] = useState<RequiredDocument | null>(null);
  const chave =
    escopo === 'modalidade'
      ? ['required-documents', modalityId]
      : ['limit-request-documents'];
  const esteiras = useEsteiras(escopo === 'modalidade');

  const consulta = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) =>
      escopo === 'modalidade'
        ? modalityTerms.listRequiredDocuments(
            modalityId,
            { include_inactive: true },
            signal,
          )
        : modalityTerms.listLimitDocuments({ include_inactive: true }, signal),
  });
  function atualizar() {
    void queryClient.invalidateQueries({ queryKey: chave });
  }
  const situacao = useMutation({
    mutationFn: (d: RequiredDocument) => {
      const status = d.status === 'ativo' ? 'inativo' : 'ativo';
      return escopo === 'modalidade'
        ? modalityTerms.updateRequiredDocument(modalityId, d.id, { status })
        : modalityTerms.updateLimitDocument(d.id, { status });
    },
    onSuccess: (d) => {
      toast.success(
        d.status === 'ativo' ? 'Documento ativado.' : 'Documento inativado.',
      );
      atualizar();
    },
    onError: (erro) => toast.error(erro.message),
  });
  const excluir = useMutation({
    mutationFn: (d: RequiredDocument) =>
      modalityTerms.removeRequiredDocument(modalityId, d.id),
    onSuccess: () => {
      setExcluindo(null);
      toast.success('Documento excluido.');
      atualizar();
    },
    onError: (erro) => toast.error(erro.message),
  });

  const colunas = [
    {
      key: 'nome',
      header: 'Nome',
      render: (d: RequiredDocument) => (
        <div className="flex flex-col">
          <span className="font-medium text-ink-900">{d.name}</span>
          {d.description ? (
            <span className="text-2xs text-ink-500">{d.description}</span>
          ) : null}
        </div>
      ),
    },
    ...(escopo === 'modalidade'
      ? [
          {
            key: 'esteiras',
            header: 'Esteiras',
            render: (d: RequiredDocument) =>
              nomesDasEsteiras(d.pipe_ids, esteiras.data),
          },
        ]
      : []),
    {
      key: 'obrigatorio',
      header: 'Obrigatorio',
      render: (d: RequiredDocument) => (d.is_required ? 'Sim' : 'Nao'),
    },
    {
      key: 'cadastro',
      header: 'Data de cadastro',
      render: (d: RequiredDocument) => formatDate(d.created_at),
    },
    {
      key: 'status',
      header: 'Status',
      render: (d: RequiredDocument) => <StatusBadge kind="cadastro" value={d.status} />,
    },
    {
      key: 'acoes',
      header: 'Acoes',
      render: (d: RequiredDocument) =>
        podeEditar || podeExcluir ? (
          <div className="flex gap-1">
            {podeEditar ? (
              <>
                <AcaoComIcone
                  acao="editar"
                  rotulo="Editar"
                  onClick={() => setEditando(d)}
                />
                <AcaoComIcone
                  acao={d.status === 'ativo' ? 'desativar' : 'ativar'}
                  rotulo={d.status === 'ativo' ? 'Inativar' : 'Ativar'}
                  loading={situacao.isPending && situacao.variables?.id === d.id}
                  onClick={() => situacao.mutate(d)}
                />
              </>
            ) : null}
            {podeExcluir ? (
              <AcaoComIcone
                acao="excluir"
                rotulo="Excluir"
                onClick={() => setExcluindo(d)}
              />
            ) : null}
          </div>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {podeCriar ? (
        <div className="flex justify-end">
          <Button size="sm" variant="secondary" onClick={() => setEditando('novo')}>
            {escopo === 'modalidade'
              ? 'Incluir documento da modalidade'
              : 'Incluir documento de limite'}
          </Button>
        </div>
      ) : null}
      {consulta.error ? (
        <Alert tone="danger" title="Nao foi possivel carregar">
          {consulta.error.message}
        </Alert>
      ) : (
        <DataTable
          loading={consulta.isPending}
          columns={colunas}
          items={consulta.data ?? []}
          keyOf={(d) => d.id}
          empty={<EmptyState icon="file" title="Nenhum documento nesta lista ainda" />}
        />
      )}
      {editando ? (
        <FormularioDoDocumento
          escopo={escopo}
          modalityId={modalityId}
          documento={editando === 'novo' ? null : editando}
          onFechar={() => setEditando(null)}
          onSalvo={() => {
            setEditando(null);
            toast.success('Documento salvo.');
            atualizar();
          }}
        />
      ) : null}
      <ConfirmDialog
        open={excluindo !== null}
        onClose={() => setExcluindo(null)}
        onConfirm={() => excluindo && excluir.mutate(excluindo)}
        title="Excluir documento exigido"
        message={`O documento "${excluindo?.name ?? ''}" sai da lista da modalidade. A exclusao fica no historico da modalidade.`}
        confirmLabel="Excluir"
        tone="danger"
        loading={excluir.isPending}
      />
    </div>
  );
}

/**
 * Aba "Documentos": os documentos que a modalidade exige, por esteira
 * (Core/GetProposalParameterRequestDocuments), e os "Documentos de Solicitacao de
 * Limite", que valem para a seguradora inteira
 * (PolicyHolder/GetRequestLimitParameterDocuments).
 *
 * A coluna Esteiras mostra as esteiras do documento, e o formulario as escolhe
 * (PAR-091): o documento sem esteira vale para todas. Nenhuma esteira cobra o documento
 * ainda (SUB-015 e CRD-017).
 */
export function DocumentosDaModalidade({ modalityId }: { modalityId: string }) {
  return (
    <div className="flex flex-col gap-6">
      <section aria-label="Documentos da modalidade" className="flex flex-col gap-3">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          Documentos da modalidade, por esteira
        </h4>
        <Alert tone="info" title="Lista guardada">
          A lista e parametro: nenhuma esteira ou cotacao cobra o documento ainda
          (SUB-015 e CRD-017). O documento sem esteira escolhida vale para todas.
        </Alert>
        <TabelaDeDocumentos escopo="modalidade" modalityId={modalityId} />
      </section>
      <section
        aria-label="Documentos de Solicitacao de Limite"
        className="flex flex-col gap-3 border-t border-line pt-4"
      >
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
          Documentos de Solicitacao de Limite
        </h4>
        <p className="text-sm text-ink-600">
          Esta lista vale para a seguradora inteira, nao apenas para esta modalidade.
        </p>
        <TabelaDeDocumentos escopo="solicitacao_limite" modalityId={modalityId} />
      </section>
    </div>
  );
}
